import React from 'react';
import { Loan, Queue } from '../types';
import { Search, Eye, Hand, AlertTriangle, User } from 'lucide-react';
interface DataTableProps {
  queue: Queue;
  loans: Loan[];
  selectedLoanId: string | null;
  onSelectLoan: (id: string) => void;
  onClaimLoan?: (id: string) => void;
  onRequestTakeOver?: (id: string) => void;
}
const QUEUE_DESCRIPTIONS: Record<string, string> = {
  Credit_Pulled_10_Days: 'Loans stuck after credit pull for 10+ days',
  App_Out_10_Days: 'Applications sent but not returned for 10+ days',
  Package_Back_10_Days: 'Packages received but not processed for 10+ days',
  Withdrawn_10_Days: 'Withdrawn loans needing a second review',
  X24_Hour_Re_Register_List: 'Loans withdrawn and not re-registered within 24h',
  Credit_Alert_List_View: 'Loans with credit alerts needing attention',
  Credit_Retention_List: 'Loans requiring client retention outreach',
  My_Claimed_Loans: 'Loans you have claimed and are working on'
};
export function DataTable({
  queue,
  loans,
  selectedLoanId,
  onSelectLoan,
  onClaimLoan,
  onRequestTakeOver
}: DataTableProps) {
  const description = QUEUE_DESCRIPTIONS[queue.apiName] || 'Queue';
  const getUrgencyColor = (days: number) => {
    if (days >= 21) return 'bg-red-100 text-red-700 border-red-200';
    if (days >= 15) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (days >= 10) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };
  const getClaimStateColor = (state: string) => {
    if (state.includes('Successful')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (state.includes('In Progress')) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (state.includes('Unsuccessful')) return 'bg-slate-50 text-slate-600 border-slate-200';
    return 'bg-slate-50 text-slate-500 border-slate-200';
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const cellPadding = 'py-3 px-4';
  return <div className="flex-1 flex flex-col min-w-0 bg-white h-full" data-id="element-155">
      {/* Queue Header */}
      <div className="px-6 py-6 border-b border-slate-200 shrink-0 bg-white" data-id="element-156">
        <div className="flex items-start justify-between" data-id="element-157">
          <div data-id="element-158">
            <h1 className="text-2xl font-semibold text-slate-900 tracking-tight mb-1" data-id="element-159">
              {queue.name}
            </h1>
            <p className="text-slate-500 text-sm" data-id="element-160">{description}</p>
          </div>
          <div className="bg-slate-100 text-slate-700 px-3 py-1 rounded-full text-sm font-medium" data-id="element-161">
            {queue.count} records
          </div>
        </div>
      </div>

      {/* Simple Toolbar */}
      <div className="px-6 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center shrink-0" data-id="element-162">
        <div className="relative w-80" data-id="element-163">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-164" />
          <input type="text" placeholder="Filter by borrower or loan number..." className="h-9 w-full pl-9 pr-4 rounded-lg bg-white border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm transition-all outline-none shadow-sm" data-id="element-165" />
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto relative" data-id="element-166">
        <table className="w-full text-left border-collapse whitespace-nowrap" data-id="element-167">
          <thead className="sticky top-0 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0] z-10" data-id="element-168">
            <tr data-id="element-169">
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-170">
                Loan
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-171">
                Borrower
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-172">
                Stage
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-173">
                Relevant Date
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-174">
                Amount
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding}`} data-id="element-175">
                Status
              </th>
              <th className={`font-semibold text-xs text-slate-500 uppercase tracking-wider ${cellPadding} text-right`} data-id="element-176">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-177">
            {loans.length === 0 ? <tr data-id="element-178">
                <td colSpan={7} className="py-16 text-center" data-id="element-179">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3" data-id="element-180">
                    <AlertTriangle className="text-slate-400" size={24} data-id="element-181" />
                  </div>
                  <h3 className="text-sm font-medium text-slate-900" data-id="element-182">
                    No loans found
                  </h3>
                  <p className="text-sm text-slate-500 mt-1" data-id="element-183">
                    This queue is currently empty.
                  </p>
                </td>
              </tr> : loans.map(loan => <tr key={loan.id} onClick={() => onSelectLoan(loan.id)} className={`group cursor-pointer transition-colors ${selectedLoanId === loan.id ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`} data-id="element-184">
                  <td className={`${cellPadding}`} data-id="element-185">
                    <div className="font-medium text-blue-600 hover:underline" data-id="element-186">
                      {loan.name}
                    </div>
                  </td>
                  <td className={`${cellPadding}`} data-id="element-187">
                    <div className="font-medium text-slate-900" data-id="element-188">
                      {loan.borrowerName}
                    </div>
                  </td>
                  <td className={`${cellPadding}`} data-id="element-189">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700" data-id="element-190">
                      {loan.stage}
                    </span>
                  </td>
                  <td className={`${cellPadding}`} data-id="element-191">
                    <div className="text-sm text-slate-900" data-id="element-192">
                      {loan.relevantDate}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-193">
                      {loan.relevantDateLabel}
                    </div>
                  </td>
                  <td className={`${cellPadding}`} data-id="element-194">
                    <div className="text-sm font-medium text-slate-900" data-id="element-195">
                      {formatCurrency(loan.amount)}
                    </div>
                  </td>
                  <td className={`${cellPadding}`} data-id="element-196">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-197">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getUrgencyColor(loan.urgencyDays)}`} data-id="element-198">
                        {loan.urgencyDays} Days Aged
                      </span>
                      {loan.claimState !== 'Unclaimed' && <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getClaimStateColor(loan.claimState)}`} data-id="element-199">
                          {loan.claimState}
                        </span>}
                    </div>
                  </td>
                  <td className={`${cellPadding} text-right`} data-id="element-200">
                    <div className="flex items-center justify-end gap-1" data-id="element-201">
                      <button onClick={e => {
                  e.stopPropagation();
                  onSelectLoan(loan.id);
                }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="View Loan" data-id="element-202">
                        <Eye size={16} data-id="element-203" />
                      </button>
                      <button disabled={loan.claimState !== 'Unclaimed' || queue.isOpportunityOnly} onClick={e => {
                  e.stopPropagation();
                  onClaimLoan?.(loan.id);
                }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent" title={queue.isOpportunityOnly ? 'Alert List - Claim This Loan (Opportunity-only update)' : loan.claimState !== 'Unclaimed' ? 'Already claimed' : 'Claim Loan'} data-id="element-204">
                        {queue.isOpportunityOnly ? <AlertTriangle size={16} data-id="element-205" /> : <Hand size={16} data-id="element-206" />}
                      </button>
                      <button disabled={loan.claimState === 'Unclaimed'} onClick={e => {
                  e.stopPropagation();
                  onRequestTakeOver?.(loan.id);
                }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-slate-400 disabled:hover:bg-transparent" title={loan.claimState === 'Unclaimed' ? 'Loan must be claimed before it can be taken over' : 'Take Over'} data-id="element-207">
                        <User size={16} data-id="element-208" />
                      </button>
                    </div>
                  </td>
                </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}